import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaHistory, FaArrowLeft, FaTrophy, FaChartLine, FaStar, FaMedal, FaClock, FaCalendarAlt } from 'react-icons/fa';
import { getUserQuizAttempts } from '../../services/quizAttempts';
import { getUserStats } from '../../services/quizService';
import { getUserXPData } from '../../services/xpService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import './QuizHistory.css';

const QuizHistory = ({ user }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [attempts, setAttempts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        totalAttempts: 0,
        averageScore: 0,
        bestScore: 0,
        totalXP: 0,
        streak: 0
    });
    const [selectedTimeFrame, setSelectedTimeFrame] = useState('all');

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                setLoading(true);
                if (!user?.uid) {
                    navigate('/signin');
                    return;
                }

                // Fetch user's quiz attempts, stats, and XP data
                const [userAttempts, userStats, xpData] = await Promise.all([
                    getUserQuizAttempts(user.uid, 100),
                    getUserStats(user.uid),
                    getUserXPData(user.uid)
                ]);

                setAttempts(userAttempts);

                // Calculate stats
                if (userAttempts.length > 0) {
                    const scores = userAttempts.map(attempt => attempt.score);
                    setStats({
                        totalAttempts: userAttempts.length,
                        averageScore: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
                        bestScore: Math.max(...scores),
                        totalXP: xpData.totalXP,
                        streak: xpData.streak
                    });
                }
            } catch (error) {
                console.error('Error fetching quiz history:', error);
                setError('Failed to load quiz history. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [user, navigate]);

    const handleBack = () => {
        navigate('/dashboard');
    };

    const handleAttemptClick = (attempt) => {
        navigate('/quiz', {
            state: {
                subject: attempt.subject,
                grade: attempt.grade,
                topic: attempt.topic,
                subtopic: attempt.subtopic
            }
        });
    };

    const filterAttempts = () => {
        const now = new Date();
        const filtered = attempts.filter(attempt => {
            const attemptDate = attempt.timestamp?.toDate();
            if (!attemptDate) return false;

            switch (selectedTimeFrame) {
                case 'today':
                    return attemptDate.toDateString() === now.toDateString();
                case 'week':
                    const weekAgo = new Date(now.setDate(now.getDate() - 7));
                    return attemptDate >= weekAgo;
                case 'month':
                    const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
                    return attemptDate >= monthAgo;
                default:
                    return true;
            }
        });
        return filtered;
    };

    const getScoreColor = (score) => {
        if (score >= 90) return '#2ecc71';
        if (score >= 70) return '#3498db';
        if (score >= 50) return '#f1c40f';
        return '#e74c3c';
    };

    if (loading) {
        return (
            <div className="quiz-history">
                <div className="history-header">
                    <button className="back-button" onClick={handleBack}>
                        <FaArrowLeft /> Back to Dashboard
                    </button>
                    <h1>Quiz History</h1>
                </div>
                <div className="history-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading your quiz history...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="quiz-history">
                <div className="history-header">
                    <button className="back-button" onClick={handleBack}>
                        <FaArrowLeft /> Back to Dashboard
                    </button>
                    <h1>Quiz History</h1>
                </div>
                <div className="history-error">
                    <div className="error-icon">⚠️</div>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    const filteredAttempts = filterAttempts();

    return (
        <div className="quiz-history">
            <div className="history-header">
                <button className="back-button" onClick={handleBack}>
                    <FaArrowLeft /> Back to Dashboard
                </button>
                <h1>Quiz History</h1>
            </div>

            <div className="history-stats">
                <div className="stat-card streak-card">
                    <FaMedal className="stat-icon" />
                    <div className="stat-content">
                        <h3>Current Streak</h3>
                        <p className="stat-value">{stats.streak}</p>
                        <span className="stat-label">days</span>
                    </div>
                </div>
                <div className="stat-card xp-card">
                    <FaStar className="stat-icon" />
                    <div className="stat-content">
                        <h3>Total XP</h3>
                        <p className="stat-value">{stats.totalXP}</p>
                        <span className="stat-label">points</span>
                    </div>
                </div>
                <div className="stat-card attempts-card">
                    <FaHistory className="stat-icon" />
                    <div className="stat-content">
                        <h3>Total Attempts</h3>
                        <p className="stat-value">{stats.totalAttempts}</p>
                        <span className="stat-label">quizzes</span>
                    </div>
                </div>
                <div className="stat-card score-card">
                    <FaChartLine className="stat-icon" />
                    <div className="stat-content">
                        <h3>Average Score</h3>
                        <p className="stat-value">{stats.averageScore}%</p>
                        <span className="stat-label">accuracy</span>
                    </div>
                </div>
                <div className="stat-card best-card">
                    <FaTrophy className="stat-icon" />
                    <div className="stat-content">
                        <h3>Best Score</h3>
                        <p className="stat-value">{stats.bestScore}%</p>
                        <span className="stat-label">highest</span>
                    </div>
                </div>
            </div>

            <div className="history-filters">
                <button 
                    className={`filter-button ${selectedTimeFrame === 'all' ? 'active' : ''}`}
                    onClick={() => setSelectedTimeFrame('all')}
                >
                    <FaCalendarAlt /> All Time
                </button>
                <button 
                    className={`filter-button ${selectedTimeFrame === 'month' ? 'active' : ''}`}
                    onClick={() => setSelectedTimeFrame('month')}
                >
                    <FaCalendarAlt /> Last Month
                </button>
                <button 
                    className={`filter-button ${selectedTimeFrame === 'week' ? 'active' : ''}`}
                    onClick={() => setSelectedTimeFrame('week')}
                >
                    <FaCalendarAlt /> Last Week
                </button>
                <button 
                    className={`filter-button ${selectedTimeFrame === 'today' ? 'active' : ''}`}
                    onClick={() => setSelectedTimeFrame('today')}
                >
                    <FaCalendarAlt /> Today
                </button>
            </div>

            <div className="history-list">
                {filteredAttempts.length > 0 ? (
                    filteredAttempts.map((attempt, index) => (
                        <div 
                            key={attempt.id || index}
                            className="history-item"
                            onClick={() => handleAttemptClick(attempt)}
                        >
                            <div className="history-item-content">
                                <div className="history-item-header">
                                    <div className="history-item-title">
                                        <h3>{attempt.subject.charAt(0).toUpperCase() + attempt.subject.slice(1)}</h3>
                                        <span className="history-grade">Grade {attempt.grade}</span>
                                    </div>
                                    <span 
                                        className="history-score"
                                        style={{ backgroundColor: getScoreColor(attempt.score) }}
                                    >
                                        {attempt.score}%
                                    </span>
                                </div>
                                <div className="history-item-details">
                                    <p className="history-topic">{attempt.topic}</p>
                                    {attempt.subtopic && (
                                        <p className="history-subtopic">{attempt.subtopic}</p>
                                    )}
                                    <div className="history-meta">
                                        <span className="history-date">
                                            <FaClock /> {new Date(attempt.timestamp?.toDate()).toLocaleDateString()}
                                        </span>
                                        {attempt.xpEarned > 0 && (
                                            <span className="history-xp">
                                                +{attempt.xpEarned} XP
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="no-history">
                        <FaHistory className="empty-icon" />
                        <p>No quiz attempts found for this time period.</p>
                        <button 
                            className="start-quiz-button"
                            onClick={() => navigate('/quiz')}
                        >
                            Take Your First Quiz
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuizHistory; 